import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import Draggable from "react-draggable";
import { Box, makeStyles } from "@material-ui/core";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";

import GrammarStepTree from "components/NodoTree/GrammarStepTree.jsx";

import { useGrammar } from "context/grammarContext";
import { useUser } from "context/userContext";
import { useAlert } from "context/Feedback/alertContext";
import { useLoading } from "context/Feedback/loadingContext";

import { ExerciseTaskHud } from "components/Exercises";
import { useExerciseTask } from "components/Exercises/hooks/useExerciseTask";

import grammarSimConstants from "constants/Grammar/SimulateGrammar";

/**
 * Estilos do componente
 */
const useStyles = makeStyles(() => ({
  simulationContainer: {
    overflow: "hidden",
    marginTop: "20px",
    height: "60vh",
    backgroundColor: "#f9f9f9",
    border: "1px solid #ddd",
    borderRadius: "8px",
    position: "relative",
  },
  controlPanel: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
    padding: "10px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  wordDisplay: {
    fontSize: "18px",
    fontWeight: "bold",
    color: "#3f51b5",
  },
}));

/**
 * Tela de simulação da árvore de derivação de gramática
 * @author Willian Brandão
 *
 * @component
 * @returns {JSX.Element}
 */
export default function SimulateGrammar() {
  const classes = useStyles();

  const location = useLocation();
  const history = useHistory();

  const { originalGrammar } = useGrammar();
  const { language, credential, isAdmin } = useUser();
  const { setAlert } = useAlert();
  const { setLoading } = useLoading();

  const { currentTask, handleSend, handleCloseTaskHud } = useExerciseTask({
    credential: credential,
    isAdmin: isAdmin,
    language: language,
    setAlert: setAlert,
    setLoading: setLoading,
    defaultType: "GRAMMAR",
  });

  // =========================
  // STATE
  // =========================
  const [word, setWord] = useState("");
  const [results, setResults] = useState(null);
  const [scale, setScale] = useState(1);
  const [visibleCount, setVisibleCount] = useState(1);
  const [taskFromRoute, setTaskFromRoute] = useState(null);

  useEffect(() => {
    try {
      if (location.state && location.state.activeTask) {
        setTaskFromRoute(location.state.activeTask);
      } else {
        const saved = sessionStorage.getItem("active_grammar_task");
        if (saved) {
          setTaskFromRoute(JSON.parse(saved));
        }
      }
    } catch (e) {
      console.error("Erro ao recuperar task", e);
    }
  }, [location.state]);

  useEffect(() => {
    if (!location.state) {
      return;
    }
    if (!location.state.simulationResults) {
      return;
    }

    const sim = location.state.simulationResults;

    setResults(sim);

    if (sim.word) {
      setWord(sim.word);
    }

    setVisibleCount(1);
    setScale(1);
  }, [location]);

  // =========================
  // HANDLERS
  // =========================
  const closeHudAndReturn = async () => {
    sessionStorage.removeItem("active_grammar_task");
    sessionStorage.removeItem("active_grammar_response");

    setTaskFromRoute(null);

    try {
      if (handleCloseTaskHud) {
        await handleCloseTaskHud();
      }
    } catch (error) {
      console.error("Erro ao fechar HUD:", error);
    }

    let path = "/user/grammar";

    if (history.location.pathname.indexOf("/admin") !== -1) {
      path = "/admin/grammar";
    }

    history.push(path);
  };

  const zoomIn = () => {
    setScale(function (prev) {
      return prev + 0.2;
    });
  };

  const zoomOut = () => {
    setScale(function (prev) {
      if (prev > 0.4) {
        return prev - 0.2;
      }
      return prev;
    });
  };

  const nextStep = () => {
    if (!results) {
      return;
    }
    if (!results.steps) {
      return;
    }

    setVisibleCount(function (prev) {
      if (prev < results.steps.length) {
        return prev + 1;
      }
      return prev;
    });
  };

  const prevStep = () => {
    setVisibleCount(function (prev) {
      if (prev > 1) {
        return prev - 1;
      }
      return prev;
    });
  };

  const showAll = () => {
    if (!results) {
      return;
    }
    if (!results.steps) {
      return;
    }

    setVisibleCount(results.steps.length);
  };

  let taskToDisplay = currentTask;
  if (!taskToDisplay) {
    taskToDisplay = taskFromRoute;
  }

  let displayWord = word;
  if (word === " ") {
    displayWord = grammarSimConstants.simulation.emptyWord[language];
  }

  const renderTree = () => {
    if (!results) {
      return (
        <Box mt={4} textAlign="center">
          <p>{grammarSimConstants.simulation.waiting[language]}</p>
        </Box>
      );
    }

    if (!results.steps) {
      return (
        <Box mt={4} textAlign="center">
          <p>{grammarSimConstants.simulation.waiting[language]}</p>
        </Box>
      );
    }

    const visibleSteps = results.steps.slice(0, visibleCount);

    return (
      <Draggable scale={scale} cancel=".exercise-hud-wrapper">
        <div style={{ cursor: "grab", width: "max-content", padding: "50px" }}>
          <div
            style={{
              transform: "scale(" + scale + ")",
              transformOrigin: "top left",
            }}
          >
            <GrammarStepTree steps={visibleSteps} currentId={null} />
          </div>
        </div>
      </Draggable>
    );
  };

  // =========================
  // RENDER
  // =========================
  return (
    <div style={{ position: "relative", minHeight: "80vh" }}>
      {taskToDisplay && (
        <Draggable>
          <div
            className="exercise-hud-wrapper"
            style={{
              position: "fixed",
              top: 100,
              right: 30,
              zIndex: 999999,
              cursor: "grab",
              display: "inline-block",
              width: "fit-content",
            }}
          >
            <ExerciseTaskHud
              task={taskToDisplay}
              onSend={async function () {
                await handleSend(originalGrammar);
              }}
              onClose={closeHudAndReturn}
              language={language}
            />
          </div>
        </Draggable>
      )}

      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader style={{ backgroundColor: "#3f51b5" }}>
              <h4 style={{ color: "white", margin: 0 }}>
                {grammarSimConstants.simulation.title[language]}
              </h4>
            </CardHeader>

            <CardBody>
              <div className={classes.controlPanel}>
                <div className={classes.wordDisplay}>
                  {grammarSimConstants.simulation.wordLabel[language]}:{" "}
                  {displayWord}
                </div>

                <Box display="flex" gap="10px">
                  <Button onClick={zoomOut}>
                    {grammarSimConstants.controls.zoomOut[language]}
                  </Button>

                  <Button onClick={zoomIn}>
                    {grammarSimConstants.controls.zoomIn[language]}
                  </Button>

                  <div style={{ width: "20px" }} />

                  <Button onClick={prevStep}>
                    {grammarSimConstants.controls.previous[language]}
                  </Button>

                  <Button onClick={nextStep}>
                    {grammarSimConstants.controls.next[language]}
                  </Button>

                  <Button onClick={showAll}>
                    {grammarSimConstants.controls.showAll[language]}
                  </Button>
                </Box>
              </div>

              <div className={classes.simulationContainer}>{renderTree()}</div>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
